import { useLazyQuery, useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Close, Loop } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Radio,
  FormControlLabel,
  RadioGroup,
} from "@mui/material";
import { ReactElement, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

import {
  CheckComplianceBundleSlugQuery,
  CreateComplianceBundleInput,
  CreateComplianceBundleMutation,
  CreateComplianceBundleMutationVariables,
  ListTenantsQuery,
  UpdateComplianceBundleInput,
  UpdateComplianceBundleMutation,
  UpdateComplianceBundleMutationVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import {
  RhfTextField,
  RhfErrorBanner,
  RhfSelect,
} from "~frontend/components/forms/react-hook-form-components";
import {
  CHECK_COMPLIANCE_BUNDLE_SLUG_AVAILABILITY,
  CREATE_COMPLIANCE_BUNDLE,
  UPDATE_COMPLIANCE_BUNDLE,
} from "~frontend/graphql/complianceBundles";
import { GET_REGISTRATION_FOR_STAFF } from "~frontend/graphql/tenDlcRegistrations";
import { LIST_TENANTS } from "~frontend/graphql/tenant";
import { GET_TOLL_FREE_REGISTRATION } from "~frontend/graphql/tollFreeRegistrations";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import { BundleTableRow } from "./BundleTable";

const ComplianceBundleSchema = z.object({
  orgName: z.string().min(1, "Required"),
  tenDlcCampaignId: z.union([
    z.string().regex(/^(C|c)(\w|\d){6}$/, "Invalid 10DLC Campaign ID"),
    z.string().length(0, "Invalid 10DLC Campaign ID").optional(),
  ]),
  slug: z
    .string()
    .min(1, "Required")
    .regex(
      /^[a-z0-9][a-z0-9-]*[a-z0-9]$/,
      "Slug must be letters, numbers, and dashes only; no spaces or other special characters"
    ),
  emailContact: z.string().min(1, "Required").email("Invalid email"),
  messageNature: z.string().min(1, "Required"),
  messagesPerDay: z.coerce.number().positive(),
  donationRequestsEnabled: z.boolean(),
  tenantIds: z.array(z.string()).min(1, "At least one tenant is required"),
});

type ComplianceBundleSchemaType = z.infer<typeof ComplianceBundleSchema>;

export const BundleDialog = ({
  bundle,
  open,
  onClose,
  tenDlcCampaignRegistrationId,
  tollFreeRegistrationId,
}: {
  bundle?: BundleTableRow | null;
  open: boolean;
  onClose: (status?: boolean) => void;
  tenDlcCampaignRegistrationId?: string | null;
  tollFreeRegistrationId?: string | null;
}): ReactElement => {
  const getErrorMessage = useErrorMessageFormatter();

  const {
    loading,
    error,
    data: tenantsData,
  } = useQuery<ListTenantsQuery>(LIST_TENANTS);

  const [getSlugAvailability, { loading: slugLoading }] =
    useLazyQuery<CheckComplianceBundleSlugQuery>(
      CHECK_COMPLIANCE_BUNDLE_SLUG_AVAILABILITY
    );

  const [slugValid, setSlugValid] = useState<boolean | null>(null);

  const refetchQueries = tenDlcCampaignRegistrationId
    ? [GET_REGISTRATION_FOR_STAFF]
    : tollFreeRegistrationId
      ? [GET_TOLL_FREE_REGISTRATION]
      : [];

  const [addBundle] = useMutation<
    CreateComplianceBundleMutation,
    CreateComplianceBundleMutationVariables
  >(CREATE_COMPLIANCE_BUNDLE, {
    refetchQueries: refetchQueries,
    awaitRefetchQueries: true,
  });

  const handleAddBundle = async (input: CreateComplianceBundleInput) => {
    if (input.tenDlcCampaignId === "") {
      input.tenDlcCampaignId = null;
    }

    if (tenDlcCampaignRegistrationId) {
      input.tenDlcCampaignRegistrationId = tenDlcCampaignRegistrationId;
    }
    if (tollFreeRegistrationId) {
      input.tollFreeRegistrationId = tollFreeRegistrationId;
    }
    const { errors } = await addBundle({
      variables: { input },
    });

    if (errors) {
      throw errors;
    } else {
      onClose(true);
    }
  };

  const [updateBundle] = useMutation<
    UpdateComplianceBundleMutation,
    UpdateComplianceBundleMutationVariables
  >(UPDATE_COMPLIANCE_BUNDLE);
  const handleUpdateBundle = async (input: UpdateComplianceBundleInput) => {
    if (input.tenDlcCampaignId === "") {
      input.tenDlcCampaignId = null;
    }

    const { errors } = await updateBundle({
      variables: { input },
    });

    if (errors) {
      throw errors;
    } else {
      onClose();
    }
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(ComplianceBundleSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    values: {
      orgName: bundle?.orgName || "",
      tenDlcCampaignId: bundle?.tenDlcCampaignId || "",
      slug: bundle?.slug || "",
      emailContact: bundle?.emailContact || "",
      messageNature: bundle?.messageNature || "",
      messagesPerDay: bundle?.messagesPerDay || 1,
      donationRequestsEnabled: bundle?.donationRequestsEnabled || false,
      tenantIds: bundle?.bundleTenants
        ? bundle.bundleTenants
            .filter(tenant => !tenant.deletedAt)
            .map(t => t.tenantId)
        : [],
    },
  });

  const onSubmit = async (values: ComplianceBundleSchemaType) => {
    const payload = {
      ...values,
      tenDlcCampaignId: values.tenDlcCampaignId ?? null,
      messagesPerDay: Number(values.messagesPerDay),
    };

    try {
      if (bundle?.id) {
        await handleUpdateBundle({
          id: bundle.id,
          ...payload,
        });
      } else {
        await handleAddBundle(payload);
      }
      reset();
    } catch (e) {
      setError("root", { message: getErrorMessage(e) });
    }
  };

  const checkSlugAvailability = async (slug: string): Promise<void> => {
    if (!slug || !slug.length) {
      setError("slug", { message: "Slug is required" });
      return;
    }

    const { data: slugData, error: slugError } = await getSlugAvailability({
      variables: { input: { slug } },
    });

    if (slugError) {
      setError("slug", { message: getErrorMessage(slugError) });
    }

    if (slugData?.checkComplianceBundleSlug.available === false) {
      setError("slug", { message: "Slug is not available" });
      setSlugValid(false);
    } else {
      clearErrors("slug");
      setSlugValid(true);
    }
  };

  const slugInputAdornment = () => {
    if (slugLoading) {
      return (
        <Loop
          sx={{
            animation: "spin 2s linear infinite",
            "@keyframes spin": {
              "0%": {
                transform: "rotate(360deg)",
              },
              "100%": {
                transform: "rotate(0deg)",
              },
            },
          }}
        />
      );
    }

    if (slugValid === false) {
      return <Close color="error" />;
    }

    if (slugValid === true) {
      return <Check color="success" />;
    }

    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
    >
      <DialogTitle>{bundle?.id ? "Edit" : "Add"} Compliance Bundle</DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ minWidth: "400px" }}>
          <AsyncWrapper data={tenantsData} errors={error} loading={loading}>
            {({ listTenants }) => (
              <>
                <RhfErrorBanner control={control} />
                <RhfTextField
                  control={control}
                  name="orgName"
                  label="Organization Legal Name"
                  fullWidth
                />
                {!tollFreeRegistrationId && (
                  <RhfTextField
                    control={control}
                    name="tenDlcCampaignId"
                    label="10DLC Campaign ID"
                    fullWidth
                  />
                )}
                <RhfTextField
                  control={control}
                  name="slug"
                  label="Slug"
                  fullWidth
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                    checkSlugAvailability(e.target.value)
                  }
                  slotProps={{
                    input: {
                      endAdornment: slugInputAdornment(),
                    },
                  }}
                />
                <RhfTextField
                  control={control}
                  name="messageNature"
                  label="Messaging Nature"
                  fullWidth
                  multiline
                  rows={4}
                  helperText="Language explaining the nature of the messaging being sent"
                />
                <RhfTextField
                  control={control}
                  name="messagesPerDay"
                  label="Messages per Day"
                  fullWidth
                  type="number"
                />

                <FormControlLabel
                  labelPlacement="start"
                  sx={{
                    display: "flex",
                    flexDirection: "row-reverse",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    ml: 0,
                    gap: 2,
                  }}
                  control={
                    <RadioGroup
                      row={true}
                      value={watch("donationRequestsEnabled")}
                      onChange={async ev => {
                        const newDonationRequestsEnabled =
                          ev.target.value === "true";
                        setValue(
                          "donationRequestsEnabled",
                          newDonationRequestsEnabled
                        );
                      }}
                    >
                      <FormControlLabel
                        value={true}
                        control={<Radio />}
                        label="Yes"
                        sx={{
                          flexBasis: `calc(33.33% - 10px)`,
                        }}
                      />

                      <FormControlLabel
                        value={false}
                        control={<Radio />}
                        label="No"
                        sx={{
                          flexBasis: `calc(33.33% - 10px)`,
                        }}
                      />
                    </RadioGroup>
                  }
                  label="Are donations requested?"
                />

                <RhfTextField
                  control={control}
                  name="emailContact"
                  label="Organization Contact Email"
                  fullWidth
                />
                <RhfSelect
                  control={control}
                  name="tenantIds"
                  label="Tenant(s)"
                  fullWidth
                  bottomLabel={errors.tenantIds?.[0]?.message || ""}
                  multiple
                >
                  {listTenants.map(({ id, name, slug }) => (
                    <MenuItem key={id} value={id}>
                      {slug}: {name}
                    </MenuItem>
                  ))}
                </RhfSelect>
              </>
            )}
          </AsyncWrapper>
        </DialogContent>

        <DialogActions>
          <Button
            disabled={isSubmitting}
            onClick={() => {
              reset();
              onClose();
            }}
            color="secondary"
          >
            Cancel
          </Button>

          <Button
            disabled={isSubmitting}
            color="primary"
            type="submit"
            loading={isSubmitting}
          >
            {bundle ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
